import React, { useState, useEffect } from 'react';
import { FaSchool, FaPlus, FaEdit, FaTrash, FaSearch, FaBan, FaCheck } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import { SCHOOL_CURRICULA } from '../../utils/constants';

/**
 * School management component for administrators
 * Allows admins to create, edit, and manage schools
 */
const SchoolManagement = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentSchool, setCurrentSchool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    curriculum: '8-4-4',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCurriculum, setFilterCurriculum] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState(null);
  const { showSuccess, showError } = useToast();
  
  // Fetch schools on component mount
  useEffect(() => {
    fetchSchools();
  }, []);
  
  // Fetch schools from API
  const fetchSchools = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/schools');
      setSchools(response.data.data);
    } catch (error) {
      console.error('Error fetching schools:', error);
      showError('Failed to load schools');
    } finally {
      setLoading(false);
    }
  };
  
  // Open modal with empty form for new school
  const handleAddNew = () => {
    setCurrentSchool(null);
    setFormData({
      name: '',
      email: '',
      phoneNumber: '',
      curriculum: '8-4-4',
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
      adminPassword: ''
    });
    setFormErrors({});
    setModalOpen(true);
  };
  
  // Open modal with school data for editing
  const handleEdit = (school) => {
    setCurrentSchool(school);
    setFormData({
      name: school.name,
      email: school.email,
      phoneNumber: school.phoneNumber,
      curriculum: school.curriculum || '8-4-4',
      adminFirstName: school.adminFirstName || '',
      adminLastName: school.adminLastName || '',
      adminEmail: school.adminEmail || '',
      adminPassword: school.adminPassword || ''
    });
    setFormErrors({});
    setModalOpen(true);
  };
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear errors for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simplified validation
    const errors = {};
    if (!formData.name) errors.name = 'School name is required';
    if (!formData.email) errors.email = 'Email is required';
    if (!formData.phoneNumber) errors.phoneNumber = 'Phone number is required';
    
    // Only validate admin fields for new schools
    if (!currentSchool) {
      if (!formData.adminFirstName) errors.adminFirstName = 'Admin first name is required';
      if (!formData.adminLastName) errors.adminLastName = 'Admin last name is required';
      if (!formData.adminEmail) errors.adminEmail = 'Admin email is required';
      if (!formData.adminPassword) errors.adminPassword = 'Admin password is required';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    try {
      const schoolData = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        curriculum: formData.curriculum
      };
      
      if (currentSchool) {
        // Update existing school
        await api.put(`/admin/schools/${currentSchool._id}`, schoolData);
        showSuccess('School updated successfully');
      } else {
        // Create new school with admin details
        // Generate a unique admin phone number by adding a random suffix to the school phone number
        // This prevents duplicate phone number errors
        const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        const adminPhoneNumber = formData.phoneNumber.replace(/\d{3}$/, randomSuffix);
        
        const newSchoolData = {
          ...schoolData,
          adminFirstName: formData.adminFirstName,
          adminLastName: formData.adminLastName,
          adminEmail: formData.adminEmail,
          adminPassword: formData.adminPassword,
          adminPhoneNumber: adminPhoneNumber, // Add unique admin phone number
          subscription: {
            tier: 'ignite',
            isActive: true
          }
        };
        await api.post('/schools', newSchoolData);
        showSuccess('School created successfully');
      }
      
      // Refresh school list
      fetchSchools();
      setModalOpen(false);
    } catch (error) {
      console.error('Error saving school:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else {
        showError(error.response?.data?.message || 'Error saving school');
      }
    }
  };
  
  // Open delete confirmation modal
  const handleDeleteClick = (school) => {
    setSchoolToDelete(school);
    setDeleteModalOpen(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!schoolToDelete) return;
    
    try {
      await api.delete(`/admin/schools/${schoolToDelete._id}`);
      showSuccess('School deleted successfully');
      fetchSchools();
    } catch (error) {
      console.error('Error deleting school:', error);
      showError(error.response?.data?.message || 'Error deleting school');
    } finally {
      setDeleteModalOpen(false);
      setSchoolToDelete(null);
    }
  };
  
  // Toggle school verification status
  const handleToggleVerification = async (school) => {
    try {
      await api.put(`/admin/schools/${school._id}/verify`, {
        verified: !school.verified,
      });
      
      showSuccess(`School ${school.verified ? 'unverified' : 'verified'} successfully`);
      fetchSchools();
    } catch (error) {
      console.error('Error updating verification status:', error);
      showError(error.response?.data?.message || 'Error updating verification status');
    }
  };
  
  // Filter schools based on search and curriculum filter
  const filteredSchools = schools.filter((school) => {
    const matchesSearch = 
      school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.phoneNumber.includes(searchTerm);
      
    const matchesCurriculum = !filterCurriculum || school.curriculum === filterCurriculum;
    
    return matchesSearch && matchesCurriculum;
  });
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaSchool className="mr-2 text-primary" /> School Management
          </h1>
          <p className="text-gray-600 mt-1">
            Register and manage schools in the system
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <button
            onClick={handleAddNew}
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Add School
          </button>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              className="form-input pl-10 py-2"
              placeholder="Search schools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div>
            <select 
              className="form-input py-2"
              value={filterCurriculum}
              onChange={(e) => setFilterCurriculum(e.target.value)}
            >
              <option value="">All Curricula</option>
              {SCHOOL_CURRICULA.map((curriculum) => (
                <option key={curriculum.value} value={curriculum.value}>
                  {curriculum.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Schools Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          {filteredSchools.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No schools found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      School
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSchools.map((school) => (
                    <tr key={school._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {school.logo ? (
                              <img
                                className="h-10 w-10 rounded-full"
                                src={school.logo}
                                alt={school.name}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center">
                                {school.name.charAt(0)}
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {school.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {school.curriculum || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{school.email}</div>
                        <div className="text-sm text-gray-500">{school.phoneNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          school.verified
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {school.verified ? 'Verified' : 'Unverified'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(school)}
                          className="text-primary hover:text-primary-600 mr-3"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleToggleVerification(school)}
                          className={`${
                            school.verified ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'
                          } mr-3`}
                          title={school.verified ? 'Unverify' : 'Verify'}
                        >
                          {school.verified ? <FaBan /> : <FaCheck />}
                        </button>
                        <button
                          onClick={() => handleDeleteClick(school)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Add/Edit School Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={currentSchool ? 'Edit School' : 'Register New School'}
        size="lg"
      >
        <div className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="name" className="form-label">School Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-input ${formErrors.name ? 'border-red-500' : ''}`}
                />
                {formErrors.name && <p className="form-error">{formErrors.name}</p>}
              </div>
              
              <div>
                <label htmlFor="curriculum" className="form-label">Curriculum</label>
                <select
                  id="curriculum"
                  name="curriculum"
                  value={formData.curriculum}
                  onChange={handleChange}
                  className="form-input"
                >
                  {SCHOOL_CURRICULA.map((curriculum) => (
                    <option key={curriculum.value} value={curriculum.value}>
                      {curriculum.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="email" className="form-label">School Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-input ${formErrors.email ? 'border-red-500' : ''}`}
                />
                {formErrors.email && <p className="form-error">{formErrors.email}</p>}
              </div>
              
              <div>
                <label htmlFor="phoneNumber" className="form-label">Phone Number</label>
                <input
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className={`form-input ${formErrors.phoneNumber ? 'border-red-500' : ''}`}
                />
                {formErrors.phoneNumber && <p className="form-error">{formErrors.phoneNumber}</p>}
              </div>

              {!currentSchool && (
                <>
                  <div className="md:col-span-2 mt-4 mb-2">
                    <h3 className="text-lg font-medium text-gray-900">School Administrator Details</h3>
                    <p className="text-sm text-gray-500">Create an admin account for this school</p>
                  </div>
                  
                  <div>
                    <label htmlFor="adminFirstName" className="form-label">First Name</label>
                    <input
                      type="text"
                      id="adminFirstName"
                      name="adminFirstName"
                      value={formData.adminFirstName}
                      onChange={handleChange}
                      className={`form-input ${formErrors.adminFirstName ? 'border-red-500' : ''}`}
                    />
                    {formErrors.adminFirstName && <p className="form-error">{formErrors.adminFirstName}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="adminLastName" className="form-label">Last Name</label>
                    <input
                      type="text"
                      id="adminLastName"
                      name="adminLastName"
                      value={formData.adminLastName}
                      onChange={handleChange}
                      className={`form-input ${formErrors.adminLastName ? 'border-red-500' : ''}`}
                    />
                    {formErrors.adminLastName && <p className="form-error">{formErrors.adminLastName}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="adminEmail" className="form-label">Admin Email</label>
                    <input
                      type="email"
                      id="adminEmail"
                      name="adminEmail"
                      value={formData.adminEmail}
                      onChange={handleChange}
                      className={`form-input ${formErrors.adminEmail ? 'border-red-500' : ''}`}
                    />
                    {formErrors.adminEmail && <p className="form-error">{formErrors.adminEmail}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="adminPassword" className="form-label">Password</label>
                    <input
                      type="password"
                      id="adminPassword"
                      name="adminPassword"
                      value={formData.adminPassword}
                      onChange={handleChange}
                      className={`form-input ${formErrors.adminPassword ? 'border-red-500' : ''}`}
                    />
                    {formErrors.adminPassword && <p className="form-error">{formErrors.adminPassword}</p>}
                  </div>
                </>
              )}
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="btn-outline mr-2"
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                {currentSchool ? 'Update School' : 'Register School'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Delete"
        size="sm"
      >
        <div className="p-6">
          <p className="text-gray-700 mb-6">
            Are you sure you want to delete <strong>{schoolToDelete ? schoolToDelete.name : ''}</strong>? This action cannot be undone and will remove all associated data.
          </p>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setDeleteModalOpen(false)}
              className="btn-outline mr-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteConfirm}
              className="bg-red-500 hover:bg-red-700 text-white font-medium py-2 px-4 rounded"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SchoolManagement;
